/**
 * Pure helpers for the per-seat model (Momentum 2-5 seats, Enterprise 6-15;
 * pricing specs v2.0 / v1.0, 2026-10-13): P = paid seats, U = occupied seats,
 * price = per-seat rate x seats.
 *
 * Shared by the Billing page (seat picker on purchase, buy/reduce seats on
 * the current plan) and the Team page ("Add member" gate). Nothing here
 * reaches the network; the shapes mirror what GET /billing/seats and
 * GET /team/seats return.
 */

export interface SeatStatusLike {
  plan_name?: string | null
  per_seat_billing: boolean
  paid_seats: number
  occupied_seats: number
  committed_seats: number
  pending_paid_seats: number | null
  min_seats: number
  max_seats: number | null
  seats_available: number
  can_buy_more: boolean
  at_plan_ceiling: boolean
}

export interface SeatBoundsLike {
  seat_min: number
  seat_max: number | null
}

/** Whether a plan is bought as a seat count above one (Momentum), which is
 *  what opens the seat picker and prints the price "per seat". */
export function isPerSeatPlan(plan: SeatBoundsLike | null | undefined): boolean {
  return !!plan && plan.seat_max !== null && plan.seat_max > 1
}

/**
 * Whether `plan` has too few seats to hold a team of `committed` people.
 *
 * `seat_max: null` means NO ceiling (clampSeatPick reads it the same way), so
 * nothing is ever too big for such a plan. This deliberately does not ask
 * isPerSeatPlan: that answers "is this bought as a seat count, so show the
 * picker and print 'per seat'", which is false for BOTH a one-seat plan and
 * an unlimited one. Using it as a stand-in for "single seat" told a 3-person
 * team that an unlimited plan had too many members (AB#22386 follow-up).
 */
export function planSeatsTooFewFor(
  plan: SeatBoundsLike | null | undefined,
  committed: number,
): boolean {
  if (!plan || plan.seat_max === null) return false
  return plan.seat_max < Math.max(1, committed)
}

/**
 * Wording for a plan card that is blocked because the team is larger than the
 * plan can hold: a short button label and the fuller explanation for its tooltip.
 * One place, so the label and the hint can never disagree about the numbers.
 */
export function teamTooBigCopy(
  plan: { display_name: string; seat_max: number | null },
  teamSize: number,
): { label: string; hint: string } {
  const people = `${teamSize} ${teamSize === 1 ? 'person' : 'people'}`
  if (plan.seat_max === 1) {
    return {
      label: 'Single-user plan',
      hint:
        `${plan.display_name} is for one user and your team has ${people}. ` +
        'Choose a team plan, or remove teammates first to use this one.',
    }
  }
  return {
    label: `Up to ${plan.seat_max} users`,
    hint:
      `${plan.display_name} supports up to ${plan.seat_max} users and your team has ${people}. ` +
      'Choose a larger plan, or remove teammates first.',
  }
}

/** Total recurring charge for `seats` seats at `rateCents` per seat. */
export function seatPurchaseTotalCents(rateCents: number, seats: number): number {
  return Math.max(0, rateCents) * Math.max(1, Math.floor(seats))
}

/**
 * The seat count a purchase picker may settle on: within the plan's bounds,
 * and never fewer than the people already committed to the team (members plus
 * pending invites) -- the server refuses that too.
 */
export function clampSeatPick(plan: SeatBoundsLike, requested: number, committed = 1): number {
  const min = Math.max(plan.seat_min, Math.max(1, committed))
  const max = plan.seat_max ?? Number.MAX_SAFE_INTEGER
  const n = Number.isFinite(requested) ? Math.floor(requested) : min
  return Math.min(max, Math.max(min, n))
}

/** The default seat count to open the picker on: the smallest legal one. */
export function defaultSeatPick(plan: SeatBoundsLike, committed = 1): number {
  return clampSeatPick(plan, plan.seat_min, committed)
}

/** How many more seats may still be bought before the plan ceiling. */
export function extraSeatsPurchasable(status: SeatStatusLike): number {
  if (!status.per_seat_billing || status.max_seats === null) return 0
  return Math.max(0, status.max_seats - status.paid_seats)
}

/**
 * What "Add member" does next (spec §5):
 *  - 'invite'     U < P: a paid seat is free.
 *  - 'buy'        U = P but P is below the ceiling: buy more first.
 *  - 'enterprise' P = U = ceiling: Momentum is maxed.
 *  - 'unknown'    no status yet (roles that cannot read it, or not loaded):
 *                 send the invite and let the server answer.
 */
export type AddMemberOutcome = 'invite' | 'buy' | 'enterprise' | 'unknown'

export function addMemberOutcome(status: SeatStatusLike | null | undefined): AddMemberOutcome {
  if (!status) return 'unknown'
  if (!status.per_seat_billing) return 'invite'
  if (status.seats_available > 0) return 'invite'
  if (status.at_plan_ceiling || !status.can_buy_more) return 'enterprise'
  return 'buy'
}

/**
 * What to tell someone who has filled every seat the plan allows: Momentum
 * points up to Enterprise, Enterprise (the top plan) points to sales.
 */
export function seatCeilingMessage(status: Pick<SeatStatusLike, 'plan_name' | 'max_seats'>): string {
  const max = status.max_seats ?? 5
  if (status.plan_name === 'enterprise') {
    return `Enterprise supports up to ${max} users. Contact sales for a custom plan.`
  }
  return `Momentum supports up to ${max} users. Upgrade to Enterprise for ${max + 1}+ users.`
}

/** "3 of 4 seats used" — committed seats (members + pending invites) against paid. */
export function describeSeatUsage(status: SeatStatusLike): string {
  const used = status.committed_seats
  const paid = status.paid_seats
  return `${used} of ${paid} paid seat${paid === 1 ? '' : 's'} used`
}

/**
 * The smallest seat count a reduction may schedule: the plan minimum, or the
 * people currently seated if that is higher. null when nothing can be
 * reduced (already at that floor, or not a per-seat plan).
 */
export function minimumSeatsAfterReduction(status: SeatStatusLike): number | null {
  if (!status.per_seat_billing) return null
  const floor = Math.max(status.min_seats, status.occupied_seats)
  return floor < status.paid_seats ? floor : null
}
